import { ICONS } from 'components/Icon';
import {
  AseFieldInstanceCheckStatusEnum,
  FieldInstanceCheckScheduleStatusEnum,
  FieldInstanceCheckStatusEnum,
} from 'store/assessments/types';
import { sortBy } from 'lodash';
import { AssessmentStatusesEnum } from './types';

export type PieDatum = {
  key: string;
  label: string;
  value: number;
  displayValue?: number | null;
  displayPercent?: number | null;
  color: string;
};

export type LineGraphData = {
  title: string;
  x: { label: string; key: string }[];
  groups: { label: string; key: string; color: string }[];
  groupedValues: Record<string, Record<string, number>>;
};

export enum ReportEnum {
  checkschedule = 'checkschedule',
  checkscompleted = 'checkscompleted',
  checkstatus = 'checkstatus',
  elements = 'elements',
  assets = 'assets',
  boom = 'boom',
}

export type ReportTypes = {
  [ReportEnum.boom]: {
    columns: 'test';
    filterOptions: {};
    filterValues: {};
    structure: {};
    apiResponse: {
      type: ReportEnum.boom;
      totalCount: number;
    };
    apiVisualResponse: null;
  };
  [ReportEnum.checkscompleted]: {
    columns:
      | 'check'
      | 'status'
      | 'asset'
      | 'element'
      | 'elementLocation'
      | 'assessment';
    filterOptions: {
      organisations: { name: string; value: string }[];
      statuses: {
        name: string;
        value: AseFieldInstanceCheckStatusEnum;
        icon: ICONS;
        color: string;
      }[];
      checks: {
        name: string;
        value: string;
        deleted: boolean;
      }[];
      elements: {
        name: string;
        value: string;
        deleted: boolean;
        checks: boolean;
      }[];
      draftStatuses: {
        name: string;
        value: 'draft' | 'approved';
      }[];
    };
    filterValues: {
      organisationIds: string[];
      search: string;
      dateFrom: number | null;
      dateTo: number | null;
      statuses: AseFieldInstanceCheckStatusEnum[];
      checks: string[];
      elements: string[];
      draftStatuses: ('draft' | 'approved')[];
    };
    structure: {
      assessment: {
        reference: string;
        customReference: string;
        typeName: string;
        assessedDate: number;
        status: {
          id: AssessmentStatusesEnum;
          name: string;
          color: string;
          ordering: number;
        };
      };
      status: {
        id: AseFieldInstanceCheckStatusEnum;
        name: string;
        color: string;
        icon: ICONS;
        ordering: number;
      };
      asset: {
        id: string;
        reference: string;
        customReference: string;
        name: string;
        postcode: string;
      };
      organisation: {
        id: string;
        name: string;
      };
      check: {
        id: string;
        name: string;
      };
      element: {
        id: string;
        reference: string;
        customReference: string;
        type: string;
        typeSingular: string;
        locationInfo: { label: string; value: string }[];
      };
    };
    apiResponse: {
      type: ReportEnum.checkscompleted;
      totalCount: number;
      data: {
        id: string;
        // assessedDate: number;
        elementId: string;
        assetId: string;
        organisationId: string;
        checkId: string;
        statusId: AseFieldInstanceCheckStatusEnum;
        assessmentId: string;
      }[];
      expandedData: {
        assessments: Record<
          string,
          {
            reference: string;
            customReference: string;
            typeName: string;
            assessedDate: number;
            statusId: AssessmentStatusesEnum;
          }
        >;
        assessmentStatuses: Record<
          AssessmentStatusesEnum,
          {
            name: string;
            color: string;
            ordering: number;
          }
        >;
        statuses: Record<
          AseFieldInstanceCheckStatusEnum,
          {
            name: string;
            color: string;
            icon: ICONS;
            ordering: number;
          }
        >;
        elements: Record<
          string,
          {
            reference: string;
            customReference: string;
            type: string;
            typeSingular: string;
            locationInfo: { label: string; value: string }[];
          }
        >;
        assets: Record<
          string,
          {
            reference: string;
            customReference: string;
            name: string;
            postcode: string;
          }
        >;
        organisations: Record<
          string,
          {
            name: string;
          }
        >;
        checks: Record<
          string,
          {
            name: string;
          }
        >;
      };
    };
    apiVisualResponse: null;
  };
  [ReportEnum.checkschedule]: {
    columns: 'check' | 'status' | 'asset' | 'element' | 'elementLocation';
    filterOptions: {
      organisations: { name: string; value: string }[];
      statuses: {
        name: string;
        value: FieldInstanceCheckScheduleStatusEnum;
        icon: ICONS;
        color: string;
      }[];
      expiredStatuses: {
        name: string;
        value: 'current' | 'expired';
      }[];
      checks: {
        name: string;
        value: string;
        deleted: boolean;
      }[];
      elements: {
        name: string;
        value: string;
        deleted: boolean;
        checks: boolean;
      }[];
    };
    filterValues: {
      organisationIds: string[];
      search: string;
      dateFrom: number | null;
      dateTo: number | null;
      statuses: FieldInstanceCheckScheduleStatusEnum[];
      expiredStatuses: ('current' | 'expired')[];
      checks: string[];
      elements: string[];
    };
    structure: {
      dateFrom: number;
      dateTo: number;
      expired: boolean;
      attemptedCount: number;
      completedCount: number;
      status: {
        id: FieldInstanceCheckScheduleStatusEnum;
        name: string;
        color: string;
        icon: ICONS;
        ordering: number;
      };
      asset: {
        id: string;
        reference: string;
        customReference: string;
        name: string;
        postcode: string;
      };
      organisation: {
        id: string;
        name: string;
      };
      check: {
        id: string;
        name: string;
      };
      element: {
        id: string;
        reference: string;
        customReference: string;
        type: string;
        typeSingular: string;
        locationInfo: { label: string; value: string }[];
      };
    };
    apiResponse: {
      type: ReportEnum.checkschedule;
      totalCount: number;
      data: {
        id: string;
        elementId: string;
        assetId: string;
        organisationId: string;
        checkId: string;
        dateFrom: number;
        dateTo: number;
        statusId: FieldInstanceCheckScheduleStatusEnum;
        expired: boolean;
        attemptedCount: number;
        completedCount: number;
      }[];
      expandedData: {
        statuses: Record<
          FieldInstanceCheckScheduleStatusEnum,
          {
            name: string;
            color: string;
            icon: ICONS;
            ordering: number;
          }
        >;
        elements: Record<
          string,
          {
            reference: string;
            customReference: string;
            type: string;
            typeSingular: string;
            locationInfo: { label: string; value: string }[];
          }
        >;
        assets: Record<
          string,
          {
            reference: string;
            customReference: string;
            name: string;
            postcode: string;
          }
        >;
        organisations: Record<
          string,
          {
            name: string;
          }
        >;
        checks: Record<
          string,
          {
            name: string;
          }
        >;
      };
    };
    apiVisualResponse: {
      visuals: [];
    };
  };
  [ReportEnum.checkstatus]: {
    columns: 'check' | 'asset' | 'element' | 'elementLocation' | 'status';
    filterOptions: {
      organisations: { name: string; value: string }[];
      statuses: {
        name: string;
        value: FieldInstanceCheckStatusEnum;
        icon: ICONS;
        color: string;
      }[];
      checks: {
        name: string;
        value: string;
        deleted: boolean;
      }[];
      elements: {
        name: string;
        value: string;
        deleted: boolean;
        checks: boolean;
      }[];
    };
    filterValues: {
      organisationIds: string[];
      search: string;
      statuses: FieldInstanceCheckStatusEnum[];
      checks: string[];
      elements: string[];
    };
    structure: {
      status: {
        id: FieldInstanceCheckStatusEnum;
        name: string;
        color: string;
        icon: ICONS;
        ordering: number;
      };
      asset: {
        id: string;
        reference: string;
        customReference: string;
        name: string;
        postcode: string;
      };
      organisation: {
        id: string;
        name: string;
      };
      check: {
        id: string;
        name: string;
      };
      element: {
        id: string;
        reference: string;
        customReference: string;
        type: string;
        typeSingular: string;
        locationInfo: { label: string; value: string }[];
      };
    };
    apiResponse: {
      type: ReportEnum.checkstatus;
      totalCount: number;
      data: {
        id: string;
        elementId: string;
        assetId: string;
        organisationId: string;
        checkId: string;
        statusId: FieldInstanceCheckStatusEnum;
      }[];
      expandedData: {
        statuses: Record<
          FieldInstanceCheckStatusEnum,
          {
            name: string;
            color: string;
            icon: ICONS;
            ordering: number;
          }
        >;
        elements: Record<
          string,
          {
            reference: string;
            customReference: string;
            type: string;
            typeSingular: string;
            locationInfo: { label: string; value: string }[];
          }
        >;
        assets: Record<
          string,
          {
            reference: string;
            customReference: string;
            name: string;
            postcode: string;
          }
        >;
        organisations: Record<
          string,
          {
            name: string;
          }
        >;
        checks: Record<
          string,
          {
            name: string;
          }
        >;
      };
    };
    apiVisualResponse: null;
  };
  [ReportEnum.elements]: {
    columns:
      | 'element'
      | 'elementLocation'
      | 'asset'
      | 'tasks'
      | 'checks'
      | 'checkSchedules';
    filterOptions: {
      organisations: { name: string; value: string }[];
      elements: {
        name: string;
        value: string;
        deleted: boolean;
        checks: boolean;
      }[];
      taskStatuses: { value: string; color: string; name: string }[];
      checkStatuses: {
        value: string;
        color: string;
        name: string;
      }[];
      checkScheduleStatuses: {
        value: string;
        color: string;
        name: string;
      }[];
    };
    filterValues: {
      organisationIds: string[];
      search: string;
      elements: string[];
      taskStatusesWith: string[];
      taskStatusesWithout: string[];
      checkScheduleStatuses: string[];
      checkStatuses: string[];
    };
    structure: {
      asset: {
        id: string;
        reference: string;
        customReference: string;
        name: string;
        postcode: string;
      };
      organisation: {
        id: string;
        name: string;
      };
      element: {
        id: string;
        reference: string;
        customReference: string;
        type: string;
        typeSingular: string;
        locationInfo: { label: string; value: string }[];
        qr: string | null;
        engageUrl: string;
      };
      tasks: {
        notstarted: {
          color: string;
          name: string;
          count: number;
        };
        inprogress: {
          color: string;
          name: string;
          count: number;
        };
        completed: {
          color: string;
          name: string;
          count: number;
        };
      };
      checks: {
        color: string;
        name: string;
        key: string;
        count: number;
      }[];
      checkSchedules: {
        color: string;
        name: string;
        key: string;
        count: number;
      }[];
    };
    apiResponse: {
      type: ReportEnum.elements;
      totalCount: number;
      data: {
        id: string;
        assetId: string;
        organisationId: string;
        tasksCount: {
          notstarted: number;
          inprogress: number;
          completed: number;
        };
        checksCount: {
          valid: number;
          endeavour: number;
          overdue: number;
          waiting: number;
        };
        checkSchedulesCount: {
          waiting: number;
          attempted: number;
          endeavour: number;
          completed: number;
        };
      }[];
      expandedData: {
        taskStatuses: Record<
          'notstarted' | 'inprogress' | 'completed',
          {
            color: string;
            name: string;
          }
        >;
        checkStatuses: Record<
          'valid' | 'endeavour' | 'overdue' | 'waiting',
          {
            color: string;
            name: string;
            key: 'valid' | 'endeavour' | 'overdue' | 'waiting';
            ordering: number;
          }
        >;
        checkScheduleStatuses: Record<
          'completed' | 'endeavour' | 'attempted' | 'waiting',
          {
            color: string;
            name: string;
            key: 'completed' | 'endeavour' | 'attempted' | 'waiting';
            ordering: number;
          }
        >;
        elements: Record<
          string,
          {
            reference: string;
            customReference: string;
            type: string;
            typeSingular: string;
            locationInfo: { label: string; value: string }[];
            qr: string | null;
            engageUrl: string;
          }
        >;
        assets: Record<
          string,
          {
            reference: string;
            customReference: string;
            name: string;
            postcode: string;
          }
        >;
        organisations: Record<
          string,
          {
            name: string;
          }
        >;
      };
    };
    apiVisualResponse: null;
  };
  [ReportEnum.assets]: {
    columns: 'asset' | 'address';
    filterOptions: {
      organisations: { name: string; value: string }[];
    };
    filterValues: {
      organisationIds: string[];
      search: string;
    };
    structure: {
      asset: {
        id: string;
        reference: string;
        customReference: string;
        name: string;
        parentName: string;
        addressLines: string[];
        postcode: string;
        engageUrl: string;
      };
      organisation: {
        id: string;
        name: string;
      };
    };
    apiResponse: {
      type: ReportEnum.assets;
      totalCount: number;
      data: {
        id: string;
        reference: string;
        customReference: string;
        name: string;
        parentName: string;
        addressLines: string[];
        postcode: string;
        organisationId: string;
        engageUrl: string;
      }[];
      expandedData: {
        organisations: Record<
          string,
          {
            name: string;
          }
        >;
      };
    };
    apiVisualResponse: null;
  };
};

export enum ReportVisualKeyEnum {
  checkScheduleStatusPie = 'checkScheduleStatusPie',
  checkOverallStatusPie = 'checkOverallStatusPie',
  checkCompletedPie = 'checkCompletedPie',
  checkCompletedOverTimeLine = 'checkCompletedOverTimeLine',

  elementsWithTaskStatusesPie = 'elementsWithTaskStatusesPie',
  elementsWithCheckStatusesPie = 'elementsWithCheckStatusesPie',
  elementsWithCheckScheduleStatusesPie = 'elementsWithCheckScheduleStatusesPie',
}

type ReportVisuals =
  | {
      type: ReportVisualKeyEnum.checkScheduleStatusPie;
      config: {
        title: string;
        data: PieDatum[];
      };
    }
  | {
      type: ReportVisualKeyEnum.checkOverallStatusPie;
      config: {
        title: string;
        data: PieDatum[];
      };
    }
  | {
      type: ReportVisualKeyEnum.checkCompletedPie;
      config: {
        title: string;
        data: PieDatum[];
      };
    }
  | {
      type: ReportVisualKeyEnum.checkCompletedOverTimeLine;
      config: {
        dataSets: {
          months: LineGraphData;
        };
      };
    }
  | {
      type: ReportVisualKeyEnum.elementsWithTaskStatusesPie;
      config: {
        title: string;
        data: PieDatum[];
      };
    }
  | {
      type: ReportVisualKeyEnum.elementsWithCheckStatusesPie;
      config: {
        title: string;
        data: PieDatum[];
      };
    }
  | {
      type: ReportVisualKeyEnum.elementsWithCheckScheduleStatusesPie;
      config: {
        title: string;
        data: PieDatum[];
      };
    };

export type ReportData<T extends ReportEnum = ReportEnum> = {
  key: string;
  isLoading: number;
  // Basics
  pageNumber: number;
  id: string;
  name: string;
  description: string;
  type: T;
  typeName: string;
  typeIsDefault: boolean;
  loaded: boolean;
  // Filtering
  filterOptions: ReportTypes[T]['filterOptions'];
  filterValues: ReportTypes[T]['filterValues'];
  draftFilterValues: ReportTypes[T]['filterValues'];
  // Actual data
  data: Record<string, ReportTypes[T]['structure']>;
  order: string[];
  totalCount: number;
  // Visuals
  visuals: ReportVisuals[];
  // visuals: ReportVisual[];
};

export function reportCheckScheduleReducer<
  ReportType extends ReportTypes[ReportEnum.checkschedule],
>(
  response: ReportType['apiResponse'],
): { order: string[]; data: Record<string, ReportType['structure']> } {
  const order: string[] = [];
  const data: Record<string, ReportType['structure']> = {};
  response.data.forEach((datum) => {
    // Add to order
    order.push(datum.id);
    // Add to data
    const asset = response.expandedData.assets[datum.assetId]!;
    const organisation =
      response.expandedData.organisations[datum.organisationId]!;
    const element = response.expandedData.elements[datum.elementId]!;
    const check = response.expandedData.checks[datum.checkId]!;
    const status = response.expandedData.statuses[datum.statusId]!;
    data[datum.id] = {
      attemptedCount: datum.attemptedCount,
      completedCount: datum.completedCount,
      dateFrom: datum.dateFrom,
      dateTo: datum.dateTo,
      expired: datum.expired,
      element: {
        id: datum.elementId,
        reference: element.reference,
        customReference: element.customReference,
        type: element.type,
        typeSingular: element.typeSingular,
        locationInfo: element.locationInfo,
      },
      check: {
        id: datum.checkId,
        name: check.name,
      },
      status: {
        id: datum.statusId,
        name: status.name,
        color: status.color,
        icon: status.icon,
        ordering: status.ordering,
      },
      asset: {
        id: datum.assetId,
        reference: asset.reference,
        customReference: asset.customReference,
        name: asset.name,
        postcode: asset.postcode,
      },
      organisation: {
        id: datum.organisationId,
        name: organisation.name,
      },
    };
  });
  return { order, data };
}

export function reportCheckCompletedReducer<
  ReportType extends ReportTypes[ReportEnum.checkscompleted],
>(
  response: ReportType['apiResponse'],
): { order: string[]; data: Record<string, ReportType['structure']> } {
  const order: string[] = [];
  const data: Record<string, ReportType['structure']> = {};
  response.data.forEach((datum) => {
    // Add to order
    order.push(datum.id);
    // Add to data
    const asset = response.expandedData.assets[datum.assetId]!;
    const organisation =
      response.expandedData.organisations[datum.organisationId]!;
    const element = response.expandedData.elements[datum.elementId]!;
    const check = response.expandedData.checks[datum.checkId]!;
    const status = response.expandedData.statuses[datum.statusId]!;
    const assessment = response.expandedData.assessments[datum.assessmentId]!;
    const assessmentStatus =
      response.expandedData.assessmentStatuses[assessment.statusId]!;
    data[datum.id] = {
      assessment: {
        reference: assessment.reference,
        customReference: assessment.customReference,
        assessedDate: assessment.assessedDate,
        typeName: assessment.typeName,
        status: {
          id: assessment.statusId,
          name: assessmentStatus.name,
          color: assessmentStatus.color,
          ordering: assessmentStatus.ordering,
        },
      },
      element: {
        id: datum.elementId,
        reference: element.reference,
        customReference: element.customReference,
        type: element.type,
        typeSingular: element.typeSingular,
        locationInfo: element.locationInfo,
      },
      check: {
        id: datum.checkId,
        name: check.name,
      },
      status: {
        id: datum.statusId,
        name: status.name,
        color: status.color,
        icon: status.icon,
        ordering: status.ordering,
      },
      asset: {
        id: datum.assetId,
        reference: asset.reference,
        customReference: asset.customReference,
        name: asset.name,
        postcode: asset.postcode,
      },
      organisation: {
        id: datum.organisationId,
        name: organisation.name,
      },
    };
  });
  return { order, data };
}

export function reportCheckStatusReducer<
  ReportType extends ReportTypes[ReportEnum.checkstatus],
>(
  response: ReportType['apiResponse'],
): { order: string[]; data: Record<string, ReportType['structure']> } {
  const order: string[] = [];
  const data: Record<string, ReportType['structure']> = {};
  response.data.forEach((datum) => {
    // Add to order
    order.push(datum.id);
    // Add to data
    const asset = response.expandedData.assets[datum.assetId]!;
    const organisation =
      response.expandedData.organisations[datum.organisationId]!;
    const element = response.expandedData.elements[datum.elementId]!;
    const check = response.expandedData.checks[datum.checkId]!;
    const status = response.expandedData.statuses[datum.statusId]!;
    data[datum.id] = {
      element: {
        id: datum.elementId,
        reference: element.reference,
        customReference: element.customReference,
        type: element.type,
        typeSingular: element.typeSingular,
        locationInfo: element.locationInfo,
      },
      check: {
        id: datum.checkId,
        name: check.name,
      },
      status: {
        id: datum.statusId,
        name: status.name,
        color: status.color,
        icon: status.icon,
        ordering: status.ordering,
      },
      asset: {
        id: datum.assetId,
        reference: asset.reference,
        customReference: asset.customReference,
        name: asset.name,
        postcode: asset.postcode,
      },
      organisation: {
        id: datum.organisationId,
        name: organisation.name,
      },
    };
  });
  return { order, data };
}

export function reportElementsReducer<
  ReportType extends ReportTypes[ReportEnum.elements],
>(
  response: ReportType['apiResponse'],
): { order: string[]; data: Record<string, ReportType['structure']> } {
  const order: string[] = [];
  const data: Record<string, ReportType['structure']> = {};
  response.data.forEach((datum) => {
    // Add to order
    order.push(datum.id);
    // Add to data
    const asset = response.expandedData.assets[datum.assetId]!;
    const organisation =
      response.expandedData.organisations[datum.organisationId]!;
    const element = response.expandedData.elements[datum.id]!;
    const orderedCheckStatuses = sortBy(
      Object.values(response.expandedData.checkStatuses),
      'ordering',
    );
    const orderedCheckScheduleStatuses = sortBy(
      Object.values(response.expandedData.checkScheduleStatuses),
      'ordering',
    );
    data[datum.id] = {
      element: {
        id: datum.id,
        reference: element.reference,
        customReference: element.customReference,
        type: element.type,
        typeSingular: element.typeSingular,
        locationInfo: element.locationInfo,
        qr: element.qr,
        engageUrl: element.engageUrl,
      },
      asset: {
        id: datum.assetId,
        reference: asset.reference,
        customReference: asset.customReference,
        name: asset.name,
        postcode: asset.postcode,
      },
      organisation: {
        id: datum.organisationId,
        name: organisation.name,
      },
      tasks: {
        notstarted: {
          color: response.expandedData.taskStatuses.notstarted.color,
          name: response.expandedData.taskStatuses.notstarted.name,
          count: datum.tasksCount.notstarted,
        },
        inprogress: {
          color: response.expandedData.taskStatuses.inprogress.color,
          name: response.expandedData.taskStatuses.inprogress.name,
          count: datum.tasksCount.inprogress,
        },
        completed: {
          color: response.expandedData.taskStatuses.completed.color,
          name: response.expandedData.taskStatuses.completed.name,
          count: datum.tasksCount.completed,
        },
      },
      checks: orderedCheckStatuses.map((item) => ({
        name: item.name,
        color: item.color,
        key: item.key,
        count: datum.checksCount[item.key] || 0,
      })),
      checkSchedules: orderedCheckScheduleStatuses.map((item) => ({
        name: item.name,
        color: item.color,
        key: item.key,
        count: datum.checkSchedulesCount[item.key] || 0,
      })),
    };
  });
  return { order, data };
}

export function reportAssetsReducer<
  ReportType extends ReportTypes[ReportEnum.assets],
>(
  response: ReportType['apiResponse'],
): { order: string[]; data: Record<string, ReportType['structure']> } {
  const order: string[] = [];
  const data: Record<string, ReportType['structure']> = {};
  response.data.forEach((datum) => {
    // Add to order
    order.push(datum.id);
    // Add to data
    const organisation =
      response.expandedData.organisations[datum.organisationId]!;
    data[datum.id] = {
      asset: {
        id: datum.id,
        reference: datum.reference,
        customReference: datum.customReference,
        name: datum.name,
        parentName: datum.parentName,
        addressLines: datum.addressLines,
        postcode: datum.postcode,
        engageUrl: datum.engageUrl,
      },
      organisation: {
        id: datum.organisationId,
        name: organisation.name,
      },
    };
  });
  return { order, data };
}
